import React, { useCallback, useState, useRef, useEffect } from "react";
import usePageTitle from "../usePageTitle";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Checkbox } from "primereact/checkbox";
import "./GenerateForms.css";
import { Divider } from "primereact/divider";
import GenerateFieldsDialog from "./GenerateFieldsDialog";
import { RadioButton } from "primereact/radiobutton";
import { Calendar } from "primereact/calendar";
import { FileUpload } from "primereact/fileupload";
import { Editor } from "primereact/editor";
import { InputTextarea } from "primereact/inputtextarea";
import { ConfirmDialog } from "primereact/confirmdialog";
import { InputSwitch } from "primereact/inputswitch";
import EditFieldSidebar from "./EditFieldSidebar";
import { Toast } from "primereact/toast";
import { createForm, fetchFormsSets } from "./api";
import { fetchForms } from "../FormsList/api";
import { useNavigate } from "react-router-dom";
import { Dialog } from "primereact/dialog";
import FormPreview from "./FormPreview";
import DependenciesDialog from "./DependenciesDialog";
import MapDialog from "./MapDialog";
import { MultiSelect } from "primereact/multiselect";

const GenerateForms = () => {
  usePageTitle("Generate Forms - SkeyeForm™");
  const navigate = useNavigate();
  const [createFieldDialogVisible, setCreateFieldDialogVisible] =
    useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [fields, setFields] = useState([]);
  const [formName, setFormName] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [formSchema, setFormSchema] = useState([]);
  const [formSet, setFormSet] = useState(null);
  const [formSets, setFormSets] = useState([]);
  const [visible, setVisible] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(null);
  const [editSidebarVisible, setEditSidebarVisible] = useState(false);
  const [moveInterval, setMoveInterval] = useState(null);
  const [fieldToEdit, setFieldToEdit] = useState(null);
  const toast = useRef(null);
  const [formsList, setFormsList] = useState([]);
  const [groupVisibility, setGroupVisibility] = useState({});
  const [groups, setGroups] = useState([]);
  const [dependenciesDialogVisible, setDependenciesDialogVisible] =
    useState(false);
  // eslint-disable-next-line
  const [depsConf, setDepsConf] = useState([]);
  const [selectedCoordinates, setSelectedCoordinates] = useState({});
  const [mapDialogVisible, setMapDialogVisible] = useState(false);
  const [mapDialogField, setMapDialogField] = useState(null);

  const handleOpenMapDialog = (field) => {
    setMapDialogField(field);
    setMapDialogVisible(true);
  };

  const handleMapSave = (positions) => {
    if (!mapDialogField) return;
    // persist selected coordinates in local state keyed by field.name
    setSelectedCoordinates((prev) => ({
      ...prev,
      [mapDialogField.name]: positions,
    }));

    // also update the current fields/formSchema so UI and saved schema carry the value
    setFields((prev) =>
      prev.map((f) =>
        f.name === mapDialogField.name ? { ...f, value: positions } : f,
      ),
    );
    setFormSchema((prev) =>
      prev.map((f) =>
        f.name === mapDialogField.name ? { ...f, value: positions } : f,
      ),
    );

    setMapDialogVisible(false);
    setMapDialogField(null);
  };

  const normalizeOptions = (opts) => {
    if (!Array.isArray(opts)) return null;
    const normalized = opts.flatMap((opt) => {
      if (
        opt &&
        typeof opt === "object" &&
        ("option" in opt || "value" in opt)
      ) {
        const optVals = Array.isArray(opt.option) ? opt.option : [opt.option];
        const valVals = Array.isArray(opt.value) ? opt.value : [opt.value];
        if (optVals.length === valVals.length) {
          return optVals.map((o, i) => ({
            option: String(o),
            value: String(valVals[i]),
          }));
        } else {
          return optVals.map((o, i) => ({
            option: String(o),
            value: String(valVals[i] ?? o),
          }));
        }
      } else {
        return [{ option: String(opt), value: String(opt) }];
      }
    });
    return normalized.length ? normalized : null;
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        const [forms, sets] = await Promise.all([
          fetchForms(),
          fetchFormsSets(),
        ]);
        setFormsList(forms);
        setFormSets(sets);
      } catch (error) {
        console.error("Error loading data:", error);
      }
    };
    loadData();
  }, []);

  const handleClick = () => {
    setCreateFieldDialogVisible(true);
  };

  const handleSaveField = (fieldData, isGroupUpdate = false) => {
    if (Array.isArray(fieldData)) {
      setFields(fieldData);
      setFormSchema(fieldData);

      if (!isGroupUpdate) {
        setCreateFieldDialogVisible(false);
      }
    } else {
      const group = groups.find((g) => g.title === fieldData.group);
      fieldData.fieldsPerRow = group ? group.fieldsPerRow : 1;

      setFields([...fields, fieldData]);
      setFormSchema((prevSchema) => [...prevSchema, fieldData]);
      setCreateFieldDialogVisible(false);
    }
  };

  const handleEditorChange = (e) => {
    setFormDescription(e.htmlValue);
  };
  const slugify = (text) => {
    return text
      .toString()
      .toLowerCase()
      .replace(/[^a-z0-9\u0621-\u064A\u0660-\u0669\-_]+/g, "_");
  };

  const checkFormExists = (normalizedNewName, formsList = []) => {
    const date = new Date().toISOString().split("T")[0];
    const newLabel = `${normalizedNewName}#${date}`;

    return formsList.some(
      (form) => form.label && form.label.toLowerCase() === newLabel,
    );
  };

  const getEligibleFields = () => {
    return fields.filter(
      (field) =>
        ["dropdown", "checkbox", "radio"].includes(field.type) &&
        field.importedFromForm &&
        field.importedFromForm.formid &&
        field.importedFromForm.fieldname,
    );
  };

  const handleCreateForm = async () => {
    if (!formName) {
      toast.current.show({
        severity: "warn",
        summary: "Warning",
        detail: "Please fill in all required fields: Form Name.",
        life: 3000,
      });
      return;
    }
    if (fields.length === 0) {
      toast.current.show({
        severity: "warn",
        summary: "Warning",
        detail: "Please add at least one field to the form.",
        life: 3000,
      });
      return;
    }
    if (checkFormExists(formName, formsList)) {
      toast.current.show({
        severity: "warn",
        summary: "Warning",
        detail: `A form with the title "${formName}" and today's date already exists. Please choose a different title.`,
        life: 3000,
      });
      return;
    }

    const normalizedNewName = slugify(formName);
    if (checkFormExists(normalizedNewName, formsList)) {
      toast.current.show({
        severity: "warn",
        summary: "Warning",
        detail: `A form with the title "${formName}" and today's date already exists. Please choose a different title.`,
        life: 3000,
      });
      return;
    }

    // Instead of sending, open dependencies dialog
    setDependenciesDialogVisible(true);
  };

  // Finalize form creation after dependencies are selected
  const handleDependenciesSave = async (depsConfArray) => {
    setDepsConf(depsConfArray);
    setDependenciesDialogVisible(false);

    const selectedFormSet = (formSets || []).find((fs) => fs.id === formSet);

    const formJsonSchema = {
      title: formName,
      description: formDescription,
      formSet: selectedFormSet ? selectedFormSet.name : null,
      properties: {},
      depsConf: depsConfArray,
    };

    fields.forEach((field) => {
      let groupValue = null;
      let fieldsPerRow = 1;

      if (field.group && typeof field.group === "object") {
        groupValue = field.group.value;
      } else if (field.group) {
        groupValue = field.group;
      }

      if (groupValue) {
        const group = groups.find((g) => g.title === groupValue);
        fieldsPerRow = group ? group.fieldsPerRow : 1;
      }

      formJsonSchema.properties[field.name] = {
        type: field.type,
        key: field.key,
        group: groupValue,
        fieldsPerRow: fieldsPerRow,
        required: field.required ? "yes" : "no",
        linked: field.linked
          ? { database: field.database, field: field.field }
          : null,
        maxLength: field.maxLength || null,
        min: field.minValue || null,
        max: field.maxValue || null,
        options: normalizeOptions(field.options),
        fileType: field.fileType || null,
        renderHeader: field.group
          ? groupVisibility[`group-${field.group}`] !== undefined
            ? groupVisibility[`group-${field.group}`]
            : true
          : null,
        updated: typeof field.updated === "boolean" ? field.updated : false,
        importedFromForm: field.importedFromForm
          ? field.importedFromForm
          : { formid: "", fieldname: "" },
      };
    });

    try {
      // eslint-disable-next-line
      const response = await createForm(formJsonSchema);
      toast.current.show({
        severity: "success",
        summary: "Success",
        detail: "Form schema created successfully",
        life: 3000,
      });
      navigate("/forms-list");
    } catch (error) {
      console.error("Error sending form schema:", error);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to create form schema",
        life: 3000,
      });
    }
  };

  const handleEdit = (index) => {
    setCurrentIndex(index);
    const fieldToEdit = fields[index];
    setFieldToEdit(fieldToEdit);
    setEditSidebarVisible(true);
  };

  const groupFields = (fields) => {
    const groupedFields = {};
    fields.forEach((field, index) => {
      const group = field.group ? `group-${field.group}` : `ungrouped-${index}`;
      if (!groupedFields[group]) {
        groupedFields[group] = [];
      }
      groupedFields[group].push(field);
    });
    return groupedFields;
  };

  const handleMove = (index, direction, group) => {
    setFields((prevFields) => {
      const newFields = [...prevFields];
      // Find all fields in the same group
      const groupFields = newFields.filter(
        (f) => f.group === group || (!f.group && !group), // Handle ungrouped fields
      );
      const currentFieldIndex = groupFields.findIndex(
        (f) => f === newFields[index],
      );
      const targetIndex =
        direction === "up" ? currentFieldIndex - 1 : currentFieldIndex + 1;

      // Only move if target position exists within the same group
      if (targetIndex >= 0 && targetIndex < groupFields.length) {
        // Find the actual indices in the main array
        const currentActualIndex = index;
        const targetActualIndex = newFields.indexOf(groupFields[targetIndex]);

        // Swap the fields
        [newFields[currentActualIndex], newFields[targetActualIndex]] = [
          newFields[targetActualIndex],
          newFields[currentActualIndex],
        ];

        // Add animation class
        newFields[currentActualIndex].swap = true;
        newFields[targetActualIndex].swap = true;

        setTimeout(() => {
          setFields((prevFields) => {
            const updatedFields = [...prevFields];
            updatedFields[currentActualIndex].swap = false;
            updatedFields[targetActualIndex].swap = false;
            return updatedFields;
          });
        }, 200);
      }
      return newFields;
    });
  };

  const handleMoveGroup = (groupName, direction) => {
    setFields((prevFields) => {
      const allGroups = Array.from(
        new Set(
          prevFields.map((f, index) =>
            f.group ? `group-${f.group}` : `ungrouped-${index}`,
          ),
        ),
      );

      const currentGroupIndex = allGroups.indexOf(groupName);
      if (currentGroupIndex < 0) {
        return prevFields;
      }

      const targetGroupIndex =
        direction === "up"
          ? Math.max(0, currentGroupIndex - 1)
          : Math.min(allGroups.length - 1, currentGroupIndex + 1);

      if (currentGroupIndex === targetGroupIndex) {
        return prevFields;
      }

      // Copy fields safely
      const newFields = [...prevFields];

      // Reorder the group names
      const arrayMove = (arr, fromIndex, toIndex) => {
        const element = arr[fromIndex];
        arr.splice(fromIndex, 1);
        arr.splice(toIndex, 0, element);
      };
      arrayMove(allGroups, currentGroupIndex, targetGroupIndex);

      let reorderedFields = [];
      allGroups.forEach((grp) => {
        reorderedFields = reorderedFields.concat(
          newFields.filter(
            (field, index) =>
              (field.group ? `group-${field.group}` : `ungrouped-${index}`) ===
              grp,
          ),
        );
      });

      return reorderedFields;
    });
  };

  const startMove = (index, direction, group) => {
    handleMove(index, direction, group);
    const interval = setInterval(
      () => handleMove(index, direction, group),
      200,
    );
    setMoveInterval(interval);
  };
  const stopMove = () => {
    clearInterval(moveInterval);
    setMoveInterval(null);
  };

  const handleDelete = (index) => {
    setCurrentIndex(index);
    setVisible(true);
  };

  const confirmDelete = () => {
    const updatedFields = fields.filter((_, i) => i !== currentIndex);
    setFields(updatedFields);

    const updatedFormSchema = formSchema.filter((_, i) => i !== currentIndex);
    setFormSchema(updatedFormSchema);

    setVisible(false);
  };

  const handleUpdateField = (updatedField) => {
    const updatedFields = fields.map((field, index) =>
      index === currentIndex ? updatedField : field,
    );
    setFields(updatedFields);

    const updatedFormSchema = formSchema.map((field, index) =>
      index === currentIndex ? updatedField : field,
    );
    setFormSchema(updatedFormSchema);

    return true;
  };

  const handleGroupsUpdate = useCallback((updatedGroups) => {
    setGroups(updatedGroups);
  }, []);

  return (
    <div className="p-grid p-dir-col table-page">
      <Toast ref={toast} />
      <div className="p-col title">
        <h1>Generate Form</h1>
        <div className="title-bar"></div>
      </div>
      <div className="table-container">
        <div className="p-field">
          <label htmlFor="name" className="block text-900 font-bold mb-2">
            Form Name :
          </label>
          <InputText
            id="name"
            placeholder="Enter name"
            value={formName}
            onChange={(e) => setFormName(e.target.value)}
            className="w-full mb-3"
          />
        </div>
        <div className="p-field">
          <label
            htmlFor="description"
            className="block text-900 font-bold mb-2"
          >
            form Description :
          </label>
          <Editor
            id="description"
            value={formDescription}
            onTextChange={(e) => handleEditorChange(e)}
            placeholder="Enter Description"
            className="w-full mb-3"
            style={{ height: "150px" }}
          />
        </div>
        <div className="p-field">
          <label htmlFor="formset" className="block text-900 font-bold mb-2">
            Formset (optional):
          </label>
          <Dropdown
            id="formset"
            placeholder="Select a Formset"
            value={formSet}
            options={formSets || []}
            optionLabel="name"
            optionValue="id"
            onChange={(e) => setFormSet(e.target.value)}
            className="w-full mb-3"
            showClear
          />
        </div>
        <Divider />
        {Object.entries(groupFields(fields)).map(([group, groupFields]) => (
          <React.Fragment key={group}>
            <div className="field-group">
              {group.startsWith("ungrouped-") ? (
                <div className="group-header">
                  <h3>field {group}</h3>
                  <div className="group-controls">
                    <Button
                      icon="pi pi-arrow-up"
                      className="p-button-text p-button-secondary btn-app"
                      onClick={() => handleMoveGroup(group, "up")}
                      disabled={
                        fields.indexOf(
                          fields.find(
                            (f, index) =>
                              (f.group
                                ? `group-${f.group}`
                                : `ungrouped-${index}`) === group,
                          ),
                        ) === 0
                      }
                    />
                    <Button
                      icon="pi pi-arrow-down"
                      className="p-button-text p-button-secondary btn-app"
                      onClick={() => handleMoveGroup(group, "down")}
                      disabled={
                        fields.indexOf(
                          fields.find(
                            (f, index) =>
                              (f.group
                                ? `group-${f.group}`
                                : `ungrouped-${index}`) === group,
                          ),
                        ) ===
                        fields.length - 1
                      }
                    />
                  </div>
                </div>
              ) : (
                <div className="group-header">
                  <h3>{group}</h3>
                  <div className="group-controls">
                    <Button
                      icon={"pi pi-eye"}
                      className={`p-button-text ${
                        groupVisibility[group]
                          ? "p-button-info"
                          : "p-button-secondary"
                      } btn-app`}
                      onClick={() => {
                        setGroupVisibility((prev) => ({
                          ...prev,
                          [group]: !prev[group],
                        }));
                      }}
                    />
                    <InputSwitch
                      checked={
                        groupVisibility[group] !== undefined
                          ? groupVisibility[group]
                          : true
                      }
                      onChange={(e) => {
                        setGroupVisibility((prev) => ({
                          ...prev,
                          [group]: e.value,
                        }));
                      }}
                      className="mr-2 mt-1"
                    />
                    <Button
                      icon="pi pi-arrow-up"
                      className="p-button-text p-button-secondary btn-app"
                      onClick={() => handleMoveGroup(group, "up")}
                      disabled={
                        fields.indexOf(
                          fields.find(
                            (f, index) =>
                              (f.group
                                ? `group-${f.group}`
                                : `ungrouped-${index}`) === group,
                          ),
                        ) === 0
                      }
                    />
                    <Button
                      icon="pi pi-arrow-down"
                      className="p-button-text p-button-secondary btn-app"
                      onClick={() => handleMoveGroup(group, "down")}
                      disabled={
                        fields.indexOf(
                          fields.find(
                            (f, index) =>
                              (f.group
                                ? `group-${f.group}`
                                : `ungrouped-${index}`) === group,
                          ),
                        ) ===
                        fields.length - 1
                      }
                    />
                  </div>
                </div>
              )}
              <div className="fields-grid">
                {groupFields.map((field) => {
                  const index = fields.indexOf(field);
                  return (
                    <div
                      key={index}
                      className={`field-container ${field.swap ? "swap" : ""}`}
                    >
                      <div className="flex justify-between items-center">
                        <label
                          htmlFor={`field-${index}`}
                          className="block text-900 font-bold mb-2 mt-3"
                        >
                          {field.key}
                        </label>
                        <div className="ml-auto">
                          <Button
                            className="p-button p-component p-button-text p-button-info btn-app"
                            icon="pi pi-pencil"
                            onClick={() => handleEdit(index)}
                          />
                          <Button
                            className="p-button p-component p-button-text p-button-secondary btn-app"
                            icon="pi pi-arrow-up"
                            onMouseDown={() =>
                              startMove(index, "up", field.group)
                            }
                            onMouseUp={stopMove}
                            onMouseLeave={stopMove}
                          />
                          <Button
                            className="p-button p-component p-button-text p-button-secondary btn-app"
                            icon="pi pi-arrow-down"
                            onMouseDown={() =>
                              startMove(index, "down", field.group)
                            }
                            onMouseUp={stopMove}
                            onMouseLeave={stopMove}
                          />
                          <Button
                            className="p-button p-component p-button-text p-button-danger btn-app"
                            icon="pi pi-trash"
                            onClick={() => handleDelete(index)}
                          />
                        </div>
                      </div>
                      {field.type === "checkbox" ? (
                        field.options.map((option, idx) => (
                          <div key={idx} className="p-field-checkbox mb-2">
                            <Checkbox
                              inputId={`field-${index}-option-${idx}`}
                              name={field.name}
                              value={option.value}
                              className="mr-2 ml-5"
                            />
                            <label
                              htmlFor={`field-${index}-option-${idx}`}
                              className="font-medium"
                            >
                              {option.option}
                            </label>
                          </div>
                        ))
                      ) : field.type === "dropdown" ? (
                        <Dropdown
                          id={`field-${index}`}
                          value={field.value}
                          options={field.options.map((option) => ({
                            label: option.option,
                            value: option.value,
                          }))}
                          placeholder={`Select ${field.name}`}
                          className="w-full mb-5"
                          filter
                        />
                      ) : field.type === "radio" ? (
                        field.options.map((option, idx) => (
                          <div key={idx} className="p-field-radiobutton">
                            <RadioButton
                              inputId={`field-${index}-option-${idx}`}
                              name={field.name}
                              value={option.value}
                              className="mr-2 mb-1 ml-5"
                            />
                            <label
                              htmlFor={`field-${index}-option-${idx}`}
                              className=" font-medium"
                            >
                              {option.option}
                            </label>
                          </div>
                        ))
                      ) : field.type === "date" ? (
                        <Calendar
                          id={`field-${index}`}
                          placeholder={`Select ${field.name}`}
                          className="w-full mb-3"
                        />
                      ) : field.type === "inputswitch" ? (
                        <InputSwitch
                          id={`field-${index}`}
                          placeholder={`Select ${field.name}`}
                          className="mb-3 ml-5"
                        />
                      ) : field.type === "fileupload" ? (
                        <FileUpload
                          id={`field-${index}`}
                          name={field.name}
                          accept={
                            field.fileType === "all"
                              ? "*"
                              : `${field.fileType}/*`
                          }
                          className="w-full mb-3 ml-5"
                          mode="basic"
                          chooseLabel="Choose"
                          chooseOptions={{
                            className: "p-button p-component",
                            style: {
                              backgroundColor: "#e29b45",
                              color: "#fff",
                              border: "none",
                            },
                          }}
                        />
                      ) : field.type === "geolocation" ? (
                        (() => {
                          const coords = selectedCoordinates[field.name];
                          const label = coords
                            ? Array.isArray(coords)
                              ? coords
                                  .map((c) =>
                                    typeof c === "number" ? c.toFixed(6) : c,
                                  )
                                  .join(", ")
                              : String(coords)
                            : "Select Coordinates";

                          return (
                            <Button
                              label={
                                coords
                                  ? `Selected: ${label}`
                                  : "Select Coordinates"
                              }
                              icon="pi pi-map-marker"
                              onClick={() => handleOpenMapDialog(field)}
                              className="w-full mb-3"
                              style={{
                                backgroundColor: "#609856",
                                color: "#fff",
                                border: "none",
                              }}
                            />
                          );
                        })()
                      ) : field.type === "textarea" ? (
                        <InputTextarea
                          id={`field-${index}`}
                          name={field.name}
                          required={field.required}
                          className="w-full mb-3"
                          mode="basic"
                          autoResize
                          rows={5}
                          cols={30}
                        />
                      ) : field.type === "multiselect" ? (
                        <MultiSelect
                          id={`field-${index}`}
                          value={field.value || []}
                          options={(field.options || []).map((option) => ({
                            label: option.option,
                            value: option.value,
                          }))}
                          placeholder={field.key || `Select ${field.name}`}
                          className="w-full mb-5"
                          display="chip"
                          filter
                        />
                      ) : (
                        <InputText
                          id={`field-${index}`}
                          type={field.type}
                          required={field.required}
                          maxLength={field.maxLength}
                          min={field.minValue}
                          max={field.maxValue}
                          placeholder={`Field ${index + 1}`}
                          className="w-full mb-3"
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            {group !== "ungrouped" && <Divider />}
          </React.Fragment>
        ))}
        <Button
          icon="pi pi-plus-circle"
          className="wide-button"
          onClick={handleClick}
        />
        <Divider />
        <div
          className="button-container-generate-forms"
          style={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <Button
            label="Preview"
            icon="pi pi-eye"
            className="p-button p-component p-button-primary create-button"
            style={{ backgroundColor: "#e29b45", border: "none" }}
            onClick={() => setPreviewVisible(true)}
          />
          <Button
            label="Create"
            icon="pi pi-check"
            className="p-button p-component p-button-primary create-button"
            style={{ backgroundColor: "#0D2E4D", border: "none" }}
            onClick={handleCreateForm}
          />
          <DependenciesDialog
            visible={dependenciesDialogVisible}
            onHide={() => setDependenciesDialogVisible(false)}
            eligibleFields={getEligibleFields()}
            onSave={handleDependenciesSave}
          />
        </div>
      </div>
      <GenerateFieldsDialog
        createFieldDialogVisible={createFieldDialogVisible}
        setCreateFieldDialogVisible={setCreateFieldDialogVisible}
        onSaveField={handleSaveField}
        existingFields={fields}
        onGroupsUpdate={handleGroupsUpdate}
      />
      <ConfirmDialog
        visible={visible}
        onHide={() => setVisible(false)}
        message="Are you sure you want to delete this field?"
        header="Confirmation"
        icon="pi pi-exclamation-triangle"
        accept={confirmDelete}
        reject={() => setVisible(false)}
      />
      <EditFieldSidebar
        visible={editSidebarVisible}
        onHide={() => setEditSidebarVisible(false)}
        field={fieldToEdit}
        onSave={handleUpdateField}
        existingFields={fields}
        groups={groups}
      />
      <Dialog
        header="Form Preview"
        visible={previewVisible}
        style={{ width: "90vw" }}
        onHide={() => setPreviewVisible(false)}
      >
        <FormPreview
          formName={formName}
          formDescription={formDescription}
          fields={fields}
        />
      </Dialog>
      <MapDialog
        visible={mapDialogVisible}
        onHide={() => {
          setMapDialogVisible(false);
          setMapDialogField(null);
        }}
        onSave={handleMapSave}
        initialValue={
          mapDialogField ? selectedCoordinates[mapDialogField.name] || [] : []
        }
      />
    </div>
  );
};

export default GenerateForms;
